import { Component, OnInit, Inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormValidation } from '../../../classes/formValidation';
import { DripApplicationRateFields } from '../calculatorSettingsForm';
import { IDripApplicationRate } from '../interfaces';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SharedUpdateService } from '../../shared/dialogs/update.service';

@Component({
	moduleId: module.id,
	selector: 'drip-application-rate-tree',
	templateUrl: 'drip-application-rate-tree.html',
	styleUrls: ['drip-application-rate-tree.scss']
})

export class DripApplicationRateTreeComponent implements OnInit {

	private _control: AbstractControl;
	private _initialValues: IDripApplicationRate;
	private _subscriptions$: Subject<boolean>;

	public form: FormGroup;

	public get f(): { [key in DripApplicationRateFields]: AbstractControl } {
		return this.form.controls as { [key in DripApplicationRateFields]: AbstractControl };
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) private _data: {
			form: FormGroup, control: AbstractControl },

		private _dialogRef: MatDialogRef<DripApplicationRateTreeComponent>,
		private _sharedUpdateService: SharedUpdateService,
	) {}

	ngOnInit(): void {
		this.form = this._data.form;
		this._control = this._data.control;
		this._subscriptions$ = new Subject();
		this._initialValues = this.form.value;
		this._setValidators();
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public calculate(e: MouseEvent): void {
		let result: number;

		if (this.f.UseTreesPerAcre.value) {
			result = this.f.EmitterDischargeRate.value *
				this.f.TreesPerAcre.value * this.f.EmittersPerTree.value / 27154
		} else {
			result = this.f.EmitterDischargeRate.value *
				this.f.EmittersPerTree.value * 43560 / (this.f.RowSpacing.value
				* this.f.InRowTreeSpacing.value * 27154);
		}

		result = Number(result.toFixed(2));
		this._control.setValue(result);
		this._clearValidators();
		this._closeDialog();
	}

	public cancel(e: MouseEvent): void {
		this.form.reset(this._initialValues);
		this._clearValidators();
		this._closeDialog();
	}

	private _setValidators(): void {
		this.f.EmitterDischargeRate.setValidators(Validators.required);
		this.f.EmittersPerTree.setValidators(Validators.required);
		this.f.UseTreesPerAcre.setValidators(Validators.required);

		this._setConditionalValidators();
		this._bindValueChanges();
	}

	private _clearValidators(): void {
		FormValidation.clearValidator(this.f.EmitterDischargeRate);
		FormValidation.clearValidator(this.f.EmittersPerTree);
		FormValidation.clearValidator(this.f.RowSpacing);
		FormValidation.clearValidator(this.f.InRowTreeSpacing);
		FormValidation.clearValidator(this.f.UseTreesPerAcre);
	}

	private _setConditionalValidators(): void {
		if (this.f.UseTreesPerAcre.value) {
			this.f.TreesPerAcre.setValidators(Validators.required);
			FormValidation.clearValidator(this.f.RowSpacing);
			FormValidation.clearValidator(this.f.InRowTreeSpacing);
		} else {
			this.f.RowSpacing.setValidators(Validators.required);
			this.f.InRowTreeSpacing.setValidators(Validators.required);
			FormValidation.clearValidator(this.f.TreesPerAcre);
		}
	}

	private _bindValueChanges(): void {
		this.f.UseTreesPerAcre.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this._setConditionalValidators();
		});
	}

	private _closeDialog() {
		this._sharedUpdateService.cancel();
		this._dialogRef.close();
	}
}
